import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Card, CardContent, Input, Modal, Select } from '@components';
import { useApiKeys } from '../hooks/useApiKeys';
import { ApiKey, ApiKeyCreated } from '../types';

const DEFAULT_EXPIRY = '90';
const EXPIRY_OPTIONS = ['30', '90', '180', '365', 'never'] as const;

const buildExpiryDate = (choice: string): string | null => {
  if (choice === 'never') return null;
  const date = new Date();
  date.setDate(date.getDate() + Number(choice));
  return date.toISOString();
};

export const ApiKeysSection: React.FC = () => {
  const { t } = useTranslation();
  const { apiKeys, loading, error, createApiKey, revokeApiKey } = useApiKeys();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [expiryChoice, setExpiryChoice] = useState(DEFAULT_EXPIRY);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createdKey, setCreatedKey] = useState<ApiKeyCreated | null>(null);
  const [copied, setCopied] = useState(false);

  const [revokeTarget, setRevokeTarget] = useState<ApiKey | null>(null);
  const [revoking, setRevoking] = useState(false);

  const activeKeys = apiKeys.filter(k => !k.revoked);

  const handleCreate = async () => {
    if (!newKeyName.trim()) return;
    setCreating(true);
    setCreateError(null);
    try {
      const created = await createApiKey({
        name: newKeyName.trim(),
        expiry_date: buildExpiryDate(expiryChoice),
      });
      setCreatedKey(created);
      setNewKeyName('');
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : t('apiKeys.createError'));
    } finally {
      setCreating(false);
    }
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setNewKeyName('');
    setExpiryChoice(DEFAULT_EXPIRY);
    setCreateError(null);
    setCreatedKey(null);
    setCopied(false);
  };

  const handleCopy = async () => {
    if (!createdKey) return;
    await navigator.clipboard.writeText(createdKey.key);
    setCopied(true);
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    try {
      await revokeApiKey(revokeTarget.reference);
      setRevokeTarget(null);
    } finally {
      setRevoking(false);
    }
  };

  const formatDate = (value: string | null) =>
    value ? new Date(value).toLocaleDateString() : '—';

  const renderExpiry = (value: string | null) => {
    if (!value) return t('apiKeys.expiresNever');
    const expired = new Date(value).getTime() < Date.now();
    return expired ? (
      <span className="text-red-600 dark:text-red-400">
        {t('apiKeys.expired')} ({formatDate(value)})
      </span>
    ) : (
      formatDate(value)
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {t('apiKeys.title')}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {t('apiKeys.subtitle')}
          </p>
        </div>
        <Button
          variant="gradient-multi"
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2"
        >
          <span className="material-icons">vpn_key</span>
          {t('apiKeys.createKey')}
        </Button>
      </div>

      {error && <p className="text-sm text-red-500">{error}</p>}

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 text-center text-gray-500 dark:text-gray-400">
              {t('common.loading')}
            </div>
          ) : activeKeys.length === 0 ? (
            <div className="p-6 text-center text-gray-500 dark:text-gray-400">
              {t('apiKeys.empty')}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700 text-left text-gray-500 dark:text-gray-400">
                    <th className="px-4 py-3 font-medium">{t('apiKeys.name')}</th>
                    <th className="px-4 py-3 font-medium">{t('apiKeys.prefix')}</th>
                    <th className="px-4 py-3 font-medium">{t('apiKeys.created')}</th>
                    <th className="px-4 py-3 font-medium">{t('apiKeys.lastUsed')}</th>
                    <th className="px-4 py-3 font-medium">{t('apiKeys.expires')}</th>
                    <th className="px-4 py-3 font-medium text-right">
                      {t('common.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {activeKeys.map(key => (
                    <tr
                      key={key.reference}
                      className="border-b border-gray-100 dark:border-gray-800"
                    >
                      <td className="px-4 py-3 text-gray-900 dark:text-white">{key.name}</td>
                      <td className="px-4 py-3">
                        <code className="text-xs font-mono text-gray-900 dark:text-gray-100 bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                          {key.prefix}…
                        </code>
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                        {formatDate(key.created)}
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                        {formatDate(key.last_used_at)}
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                        {renderExpiry(key.expiry_date)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setRevokeTarget(key)}
                          className="text-red-600 dark:text-red-400"
                        >
                          {t('apiKeys.revoke')}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create modal */}
      <Modal isOpen={showCreateModal} onClose={closeCreateModal}>
        {createdKey ? (
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
              {t('apiKeys.createdTitle')}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('apiKeys.createdWarning')}
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs font-mono text-gray-900 dark:text-gray-100 bg-gray-100 dark:bg-gray-800 px-3 py-2 rounded break-all">
                {createdKey.key}
              </code>
              <Button variant="secondary" size="sm" onClick={handleCopy}>
                {copied ? t('common.copied') : t('common.copy')}
              </Button>
            </div>
            <div className="flex justify-end">
              <Button variant="gradient-multi" onClick={closeCreateModal}>
                {t('common.done')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
              {t('apiKeys.createKey')}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('apiKeys.createDescription')}
            </p>
            <Input
              type="text"
              placeholder={t('apiKeys.namePlaceholder')}
              value={newKeyName}
              onChange={e => setNewKeyName(e.target.value)}
            />
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('apiKeys.expiresIn')}
              </label>
              <Select
                value={expiryChoice}
                onChange={value =>
                  setExpiryChoice(Array.isArray(value) ? value[0] : value)
                }
                options={EXPIRY_OPTIONS.map(option => ({
                  value: option,
                  label:
                    option === 'never'
                      ? t('apiKeys.expiresNever')
                      : t('apiKeys.expiryDays', { count: Number(option) }),
                }))}
              />
              {expiryChoice === 'never' && (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  {t('apiKeys.neverWarning')}
                </p>
              )}
            </div>
            {createError && <p className="text-sm text-red-500">{createError}</p>}
            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={closeCreateModal}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="gradient-multi"
                onClick={handleCreate}
                disabled={creating || !newKeyName.trim()}
              >
                {creating ? t('common.loading') : t('apiKeys.createKey')}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Revoke confirmation */}
      <Modal isOpen={Boolean(revokeTarget)} onClose={() => setRevokeTarget(null)}>
        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
            {t('apiKeys.revokeTitle')}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t('apiKeys.revokeWarning')}{' '}
            <Badge variant="secondary" size="sm">
              {revokeTarget?.name}
            </Badge>
          </p>
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setRevokeTarget(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={handleRevoke}
              disabled={revoking}
            >
              {revoking ? t('common.loading') : t('apiKeys.revoke')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
